/* Load discovery dialogs when requested; keep the first click and allow retry. */
(() => {
  const config = document.querySelector('script[data-ai-modal-loader]')?.dataset;
  if (!config) return;
  const features = {
    visual: { script: config.visualScript, style: config.visualStyle, global: 'duvarkaplaVisualSearch' },
    finder: { script: config.finderScript, style: config.finderStyle, global: 'duvarkaplaWallpaperFinder' },
  };
  const assets = new Map();
  const loading = new Map();
  const loaded = new Map();
  let pending;
  let dialog;
  let version = 0;

  function asset(url, style = false) {
    if (assets.has(url)) return assets.get(url);
    const promise = new Promise((resolve, reject) => {
      const node = document.createElement(style ? 'link' : 'script');
      if (style) {
        node.rel = 'stylesheet';
        node.href = url;
      } else {
        node.async = false;
        node.src = url;
      }
      node.onload = resolve;
      node.onerror = () => {
        node.remove();
        assets.delete(url);
        reject(new Error('discovery_asset_unavailable'));
      };
      document.head.appendChild(node);
    });
    assets.set(url, promise);
    return promise;
  }

  function load(name) {
    const feature = features[name];
    if (!feature?.script) return Promise.reject(new Error('discovery_unavailable'));
    if (loading.has(name)) return loading.get(name);
    // Finder results use the shared visual product-card styles too.
    const styles = [asset(config.visualStyle, true)];
    if (name === 'finder') styles.push(asset(feature.style, true));
    // The feature constructor needs the shared results class. Only request it
    // once that dependency succeeds, so a failed first download can be retried.
    const promise = Promise.all([asset(config.resultsScript), ...styles])
    .then(() => asset(feature.script)).then(() => {
      const instance = window[feature.global];
      if (!instance || instance.deferred) throw new Error('discovery_unavailable');
      loaded.set(name, instance);
      return instance;
    }).catch((error) => {
      loading.delete(name);
      throw error;
    });
    loading.set(name, promise);
    return promise;
  }

  function cancel() {
    version++;
    const trigger = pending?.returnFocus;
    pending = null;
    dialog?.close();
    if (trigger?.isConnected) trigger.focus();
  }

  function showLoading() {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.dataset.aiLoading = '';
      dialog.setAttribute('aria-label', 'Arama aracı');
      dialog.style.cssText = 'position:fixed;inset:0;margin:auto;width:min(360px,calc(100% - 40px));padding:24px;border:1px solid #ddd;border-radius:16px;background:#fff;color:#222;text-align:center;font:inherit;box-shadow:0 16px 60px #0003;';
      dialog.innerHTML = '<p role="status" aria-live="polite"></p><button type="button" data-ai-retry hidden>Tekrar dene</button> <button type="button" data-ai-cancel>Kapat</button>';
      dialog.querySelector('[data-ai-cancel]').addEventListener('click', cancel);
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); cancel(); });
      dialog.querySelector('[data-ai-retry]').addEventListener('click', () => {
        const retry = pending;
        pending = null;
        if (retry) open(retry.name, retry.trigger);
      });
      document.body.appendChild(dialog);
    }
    dialog.querySelector('[role="status"]').textContent = 'Arama aracı hazırlanıyor…';
    dialog.querySelector('[data-ai-retry]').hidden = true;
    if (!dialog.open) dialog.showModal();
  }

  function open(name, trigger = document.activeElement) {
    if (pending?.name === name && pending.trigger === trigger) return pending.promise;
    const feature = features[name];
    if (!feature?.script) return Promise.resolve(null);
    const instance = window[feature.global];
    if (loaded.has(name)) {
      if (instance.element.hidden) instance.open(trigger);
      return Promise.resolve(instance);
    }
    const token = ++version;
    const parentModal = trigger?.closest?.('details-modal');
    const request = { name, trigger, returnFocus: parentModal?.querySelector('summary') || trigger };
    pending = request;
    if (parentModal?.isOpen?.()) parentModal.close(false);
    showLoading();
    request.promise = load(name).then((ready) => {
      if (token !== version) return null;
      pending = null;
      dialog.close();
      ready.open(trigger);
      return ready;
    }).catch(() => {
      if (token !== version) return null;
      dialog.querySelector('[role="status"]').textContent = 'Arama aracı yüklenemedi. Lütfen tekrar deneyin.';
      dialog.querySelector('[data-ai-retry]').hidden = false;
      return null;
    });
    return request.promise;
  }

  window.DuvarkaplaAITools = { open, load };
  Object.entries(features).forEach(([name, feature]) => {
    if (feature.script && !window[feature.global]) {
      window[feature.global] = { deferred: true, open: (trigger) => open(name, trigger) };
    }
  });
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-visual-search-open], [data-wallpaper-finder-open]');
    if (!trigger) return;
    // Opening a native dialog here makes its submit button inert before the
    // browser's form action. The landing form opens it from its submit handler.
    if (trigger.type === 'submit' && trigger.form?.matches('[data-ai-finder-launch-form]')) return;
    if (trigger.tagName === 'A') event.preventDefault();
    open(trigger.matches('[data-wallpaper-finder-open]') ? 'finder' : 'visual', trigger);
  });
})();
